import { useContext, useEffect, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import { AText, Box, Button, Overlay } from '@alf/uikit';
import { AB_VARIANTS, useAB } from '@namespace/ab-test';
import { LOG_ACTIONS, logAction } from '@namespace/analytics';
import {
  getSessionStorageValue,
  POPUP_TYPES,
  SESSION_STORAGE_KEYS
} from '@namespace/helpers';
import { useI18n, useLocation } from '@namespace/i18n';
import { useAddSearchParams, useHistory } from '@namespace/router';
import { UserContext } from '@namespace/user';
import { useLeaveDepositConfirm } from '../../hooks/useLeaveDepositConfirm';
import { PopupContent } from './PopupContent';

import styles from './index.module.css';

export const LeaveDepositConfirmPopup = () => {
  const [user] = useContext(UserContext);
  const { id: userId } = user;
  const { t, f } = useI18n();
  const { push, goBack } = useHistory();
  const { pathname } = useLocation();
  const addSearchParams = useAddSearchParams();
  const DATE_FORMATS_TYPES = f.getFormats();
  const [isShowPopup, setIsShowPopup] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState(null);
  const [isMenuClicked, setIsMenuClicked] = useState(false);
  const [isBackButtonClicked, setIsBackButtonClicked] = useState(false);
  const variantMapping = {
    'variant-a': 'ab_deposit_pop-up_not_show',
    'variant-b': 'ab_deposit_pop-up_once_view',
    'variant-c': 'ab_deposit_pop-up_each_time_view'
  };
  const variant = useAB(AB_VARIANTS.DEPOSIT_CONFIRM_POPUP);
  const prevPathname = useMemo(
    () => getSessionStorageValue(SESSION_STORAGE_KEYS.PREV_PATHNAME, ''),
    [pathname]
  );

  useLeaveDepositConfirm({
    variant,
    setIsBackButtonClicked,
    setIsMenuClicked,
    setIsShowPopup,
    setRedirectUrl
  });

  const currentVariant = variantMapping[variant];

  const handleDepositClick = () => {
    const currentDateTime = DateTime.now().toFormat(
      DATE_FORMATS_TYPES.MESSAGES_NEW
    );
    setIsShowPopup(false);
    setRedirectUrl(null);
    logAction(LOG_ACTIONS.AB_TEST_DEPOSIT_DEPOSIT_BTN, {
      userId,
      dateTime: currentDateTime,
      testGroup: currentVariant
    });
  };

  const handleCloseClick = () => {
    const currentDateTime = DateTime.now().toFormat(
      DATE_FORMATS_TYPES.MESSAGES_NEW
    );
    logAction(LOG_ACTIONS.AB_TEST_DEPOSIT_CANCEL_BTN, {
      userId,
      dateTime: currentDateTime,
      testGroup: currentVariant
    });
    setIsShowPopup(false);
    if (redirectUrl) {
      push(redirectUrl);
      setRedirectUrl(null);
    }
    if (isMenuClicked) {
      addSearchParams({ popup: POPUP_TYPES.PROFILE_MENU }, null, false);
    }
    if (isBackButtonClicked) {
      if (!window.history.state) {
        push(prevPathname);
      } else {
        goBack();
      }
    }
  };

  useEffect(() => {
    if (!isShowPopup) {
      setRedirectUrl(null);
    }
  }, [isShowPopup]);

  return (
    <>
      {isShowPopup && (
        <Overlay classNames={{ wrapperClass: styles.overlay }}>
          <Box
            align="center"
            justify="between"
            direction="column"
            className={styles.container}
          >
            <AText align="center" size="l" breed="BU" color="txt_color_3">
              {t('web.deposit.ftd.popup.title')}
            </AText>
            <PopupContent />
            <Box className={styles.buttonsWrapper} direction="row">
              <Button
                onClick={handleCloseClick}
                className={styles.btn}
                intent="secondary"
                actionType="color1"
                size="m"
              >
                {t('web.deposit.ftd.popup.btn.close')}
              </Button>
              <Button
                onClick={handleDepositClick}
                className={styles.btn}
                intent="primary"
                actionType="color1"
                size="m"
              >
                {t('web.deposit.ftd.popup.btn.deposit')}
              </Button>
            </Box>
          </Box>
        </Overlay>
      )}
    </>
  );
};
